/**
 * Screen permissions as pure data (owner decision 2026-10-07, permissions
 * brief): no React, no i18n, unit-tested in
 * `src/utils/__tests__/screenAccess.test.ts`.
 *
 * - Every nav item (`navModel.ts`) is one screen key, `screen:<nav id>`. The
 *   server keeps the same list (`GET /api/permissions/catalog`).
 * - `GET /api/permissions/me` answers `{ isAdmin, screens, scopes }`. When the
 *   server does not know it yet (404), the role's defaults apply, which are
 *   exactly the nav model's role rules, so a new UI works against an old API.
 * - A screen the person may not open does not exist for them: not in the
 *   nav, the palette, QuickCreate, cards or links, and its address returns
 *   home silently (`ScreenGate` in App.tsx). The server enforces the data.
 */
import { navCatalog, NAV_ROLES, type NavGroupId, type NavRole } from '../navModel';

export const SCREEN_PREFIX = 'screen:';
/** "כל הפרויקטים והפיתוחים": every project, milestone and task in the tenant. */
export const SCOPE_PROJECTS_ALL = 'scope:projects_all';
/** Home is always open: it is where a blocked address lands. */
export const HOME_SCREEN = 'screen:today';
/** The permissions screen itself stays with the General Manager, whatever a group says. */
export const ADMIN_ONLY_SCREENS: ReadonlySet<string> = new Set(['screen:permissions']);
/**
 * Screens whose API the server keeps for the General Manager whatever a grant
 * says (the server catalog's `serverAdminOnly`; same list as
 * `SERVER_ADMIN_ONLY_SCREENS` in `accessGrants.service.ts`). They stay in the
 * permissions grid, disabled with an explanation, and never open for anyone
 * else, so a grant cannot show a screen whose data is refused.
 */
export const SERVER_ADMIN_ONLY_SCREENS: ReadonlySet<string> = new Set([
  'screen:cashFlow',
  'screen:portalAccess',
  'screen:employees',
  'screen:systemSettings',
  'screen:emailTemplates',
  'screen:blocklist',
  'screen:api',
  'screen:backups',
  'screen:auditTrail',
  'screen:diagnostics',
]);

/**
 * P-33c (D-185) — an internal-only employee ("עובדת פנימית בלבד") opens only
 * these screens, whatever grants say (same list as `INTERNAL_ONLY_SCREENS` in
 * `accessGrants.service.ts`). No dashboard: "היום שלי" is her home.
 */
export const INTERNAL_ONLY_SCREENS: ReadonlySet<string> = new Set([
  // FIX-B15 (QA-P33bc): no 'screen:bugReports' - its API is closed to her; the
  // "שליחת דיווח" button (avatar menu, POST /bug-reports) stays.
  'screen:today', 'screen:tasks', 'screen:attendance', 'screen:workLogs',
  // FIX-B17 (D-198 QX-148) — the meetings page, with her internal meetings only.
  'screen:meetings',
  // FQ-036 (D-196 QX-141) - discussions, internal only (no customer; the server scope filters).
  'screen:conversations',
  // D-274 §2 — "+ פרויקט חדש" of "עבודה פנימית OTE" and its milestones (the server scopes them to the company).
  'screen:projects', 'screen:milestones',
]);

/** The screens of an internal-only employee: the intersection with INTERNAL_ONLY_SCREENS. */
export function restrictScreensToInternalOnly(screens: readonly string[]): string[] {
  return screens.filter((k) => INTERNAL_ONLY_SCREENS.has(k));
}

export type AccountRole = (typeof NAV_ROLES)[number];

export const screenKey = (navId: string): string => `${SCREEN_PREFIX}${navId}`;
export const navIdOf = (key: string): string | null => (key.startsWith(SCREEN_PREFIX) ? key.slice(SCREEN_PREFIX.length) : null);

export interface ScreenDef {
  key: string;
  navId: string;
  to: string;
  group: NavGroupId;
  defaultRoles: AccountRole[];
}

let catalogCache: ScreenDef[] | null = null;

/** Every screen key, in nav order, with the roles that see it by default. */
export function screenCatalog(): ScreenDef[] {
  if (!catalogCache) {
    catalogCache = navCatalog().map((i) => ({ key: screenKey(i.id), navId: i.id, to: i.to, group: i.group, defaultRoles: i.defaultRoles }));
  }
  return catalogCache;
}

/** The scope keys the UI knows, in display order. */
export const SCOPE_KEYS = [SCOPE_PROJECTS_ALL] as const;

/** What a role sees with no groups and no personal grants (today's behaviour). */
export function roleDefaultScreens(role: NavRole): Set<string> {
  if (!role) return new Set();
  return new Set(screenCatalog().filter((s) => s.defaultRoles.includes(role)).map((s) => s.key));
}

/**
 * SEC-2a (D-91 ב5) — a customer identity (no employee profile), whatever its
 * role claim: no internal screen, its home is /portal. `is_ote_employee`
 * comes with the login; an older session without it falls back to the role.
 */
export function isPortalIdentity(user: { role?: string | null; is_ote_employee?: boolean } | null | undefined): boolean {
  if (!user) return false;
  return user.role === 'customer_viewer' || user.is_ote_employee === false;
}

/**
 * SEC-2a (D-91 ב10) — what the server says the person may do, so a button is
 * shown only when the server will accept it (`GET /api/permissions/me`).
 */
export interface MyCapabilities {
  isGeneralManager: boolean;
  isAccountManager: boolean;
  managedOrgIds: string[];
  managedProjectIds: string[];
  teamLeadProjectIds: string[];
  canManageAllLeads: boolean;
  canViewOthersAttendance: boolean;
  /** D-335 — edit other employees' attendance (the OTE manager, or an "edit" override). Absent on an older server. */
  canEditOthersAttendance?: boolean;
  canCreateEmployeeLogin: boolean;
  canUseObligations: boolean;
  canViewDebtAging: boolean;
}

/** D-335 — an employee's own page override (the permissions screen, "לפי עובד"). */
export type ScreenAccessLevel = 'hidden' | 'view' | 'edit';

export const NO_CAPABILITIES: MyCapabilities = {
  isGeneralManager: false,
  isAccountManager: false,
  managedOrgIds: [],
  managedProjectIds: [],
  teamLeadProjectIds: [],
  canManageAllLeads: false,
  canViewOthersAttendance: false,
  canCreateEmployeeLogin: false,
  canUseObligations: false,
  canViewDebtAging: false,
};

/** Capabilities before the server sends them (an older API): the GM keeps his. */
function fallbackCapabilities(role: NavRole, portalOnly: boolean): MyCapabilities {
  if (portalOnly || role !== 'admin') return NO_CAPABILITIES;
  return {
    ...NO_CAPABILITIES,
    isGeneralManager: true,
    canManageAllLeads: true,
    canViewOthersAttendance: true,
    canEditOthersAttendance: true,
    canCreateEmployeeLogin: true,
    canUseObligations: true,
    canViewDebtAging: true,
  };
}

export interface ScreenAccess {
  role: NavRole;
  /** A customer identity: no internal screen, home is /portal. */
  portalOnly: boolean;
  capabilities: MyCapabilities;
  isAdmin: boolean;
  /** P-33c (D-185): internal-work-only employee. */
  internalOnly: boolean;
  /** P-33c: the engagement "עבודה פנימית" (set for an internal-only employee). */
  internalWorkEngagementId: string | null;
  internalWorkOrganizationId: string | null;
  screens: ReadonlySet<string>;
  scopes: ReadonlySet<string>;
  /** The role's default screens; a detail page of a screen outside them keeps its old rule. */
  roleDefaults: ReadonlySet<string>;
  /** D-335 — his own page overrides (`screen:<id>` → hidden / view / edit); empty when none. */
  screenAccess?: Readonly<Record<string, ScreenAccessLevel>>;
  /** FIX-PERMS-SCOPE — the screens he sees in the scope "all employees" (read only, no money); empty when none. */
  dataScopes?: ReadonlySet<string>;
}

/** FIX-PERMS-SCOPE — does he see this screen in the scope "all employees"? */
export function seesAllEmployees(access: Pick<ScreenAccess, 'dataScopes'>, key: string): boolean {
  return access.dataScopes?.has(key) === true;
}

/** D-335 — does his own "view" / "edit" override open this screen? */
export function openedByOverride(access: Pick<ScreenAccess, 'screenAccess'>, key: string): boolean {
  const level = access.screenAccess?.[key];
  return level === 'view' || level === 'edit';
}

export interface MyPermissions {
  isAdmin: boolean;
  screens: string[];
  scopes: string[];
  portalOnly?: boolean;
  /** The server's identity answer (employee profile); absent on an older server. */
  isOteEmployee?: boolean;
  /** P-33c (D-185): internal-work-only employee. */
  internalOnly?: boolean;
  internalWorkEngagementId?: string | null;
  internalWorkOrganizationId?: string | null;
  capabilities?: MyCapabilities;
  /** D-335 — his own page overrides; absent on an older server. */
  screenAccess?: Record<string, ScreenAccessLevel>;
  /** FIX-PERMS-SCOPE — the screens he sees in the scope "all employees"; absent on an older server. */
  dataScopes?: string[];
}

/**
 * D-311 (DQ-013) - a regular employee (an OTE employee who is not the OTE
 * manager, an Account Manager, a Project Manager or a Team Lead) has no
 * customers page and no contacts page (the server leaves them out of
 * `/permissions/me` too). A customer card opened from a link (`/organizations/<id>`)
 * stays open: the screens are also taken out of his role defaults, so the
 * detail page keeps its rule from before screen permissions.
 */
export const PLAIN_EMPLOYEE_CLOSED_SCREENS: ReadonlySet<string> = new Set(['screen:organizations', 'screen:contacts']);

export function isPlainEmployee(c: MyCapabilities | undefined): boolean {
  return !!c && !c.isGeneralManager && c.managedOrgIds.length === 0
    && c.managedProjectIds.length === 0 && c.teamLeadProjectIds.length === 0;
}

/** Access from `GET /api/permissions/me`. */
export function accessFromServer(me: MyPermissions, role: NavRole, portalHint = false): ScreenAccess {
  const portalOnly = me.portalOnly === true || portalHint || role === 'customer_viewer';
  const internalOnly = !portalOnly && me.internalOnly === true;
  const plain = !portalOnly && !internalOnly && !me.isAdmin && isPlainEmployee(me.capabilities);
  // D-335: a screen his own override opens stays open.
  const screenAccess = !portalOnly && !internalOnly && !me.isAdmin ? me.screenAccess ?? {} : {};
  const withoutClosed = (keys: Iterable<string>): string[] => [...keys].filter((k) => !plain || !PLAIN_EMPLOYEE_CLOSED_SCREENS.has(k) || openedByOverride({ screenAccess }, k));
  const screens = withoutClosed(Array.isArray(me.screens) ? me.screens : []);
  return {
    role,
    portalOnly,
    capabilities: portalOnly ? NO_CAPABILITIES : me.capabilities ?? fallbackCapabilities(role, false),
    isAdmin: !portalOnly && !internalOnly && !!me.isAdmin,
    internalOnly,
    internalWorkEngagementId: internalOnly ? me.internalWorkEngagementId ?? null : null,
    internalWorkOrganizationId: internalOnly ? me.internalWorkOrganizationId ?? null : null,
    screens: new Set(internalOnly ? restrictScreensToInternalOnly(screens) : screens),
    scopes: new Set(internalOnly ? [] : Array.isArray(me.scopes) ? me.scopes : []),
    roleDefaults: new Set(withoutClosed(roleDefaultScreens(role))),
    screenAccess,
    // FIX-PERMS-SCOPE: never for the OTE manager (he has everything), a customer or an internal-only employee.
    dataScopes: new Set(!portalOnly && !internalOnly && !me.isAdmin ? me.dataScopes ?? [] : []),
  };
}

/**
 * Access when the server has no permissions endpoint yet (404) or did not
 * answer: the role's defaults, as before screen permissions. The General
 * Manager keeps every project; nobody else is promised it.
 */
export function fallbackAccess(role: NavRole, portalHint = false): ScreenAccess {
  const portalOnly = portalHint || role === 'customer_viewer';
  const defaults = roleDefaultScreens(portalOnly ? 'customer_viewer' : role);
  return {
    role,
    portalOnly,
    capabilities: fallbackCapabilities(role, portalOnly),
    isAdmin: !portalOnly && role === 'admin',
    internalOnly: false,
    internalWorkEngagementId: null,
    internalWorkOrganizationId: null,
    screens: defaults,
    scopes: new Set(!portalOnly && role === 'admin' ? [SCOPE_PROJECTS_ALL] : []),
    roleDefaults: defaults,
  };
}

/** Nothing is open yet (permissions still loading, no cached answer). */
export function noAccess(role: NavRole, portalHint = false): ScreenAccess {
  const portalOnly = portalHint || role === 'customer_viewer';
  return { role, portalOnly, capabilities: NO_CAPABILITIES, isAdmin: false, internalOnly: false, internalWorkEngagementId: null, internalWorkOrganizationId: null, screens: new Set(), scopes: new Set(), roleDefaults: roleDefaultScreens(role) };
}

export function canScreen(access: ScreenAccess, key: string): boolean {
  if (key === HOME_SCREEN) return true;
  // The portal identity (any customer login, D-91 ב5) has no internal screen,
  // whatever a grant (or an older server's answer) says: the server refuses
  // it every internal API (`authenticate`, portal-only paths).
  if (access.portalOnly || access.role === 'customer_viewer') return false;
  // P-33c (D-185): an internal-only employee has only her five screens, whatever else says.
  if (access.internalOnly) return INTERNAL_ONLY_SCREENS.has(key) && access.screens.has(key);
  if (access.isAdmin) return true;
  // D-158 (QX-88): an Account Manager opens "גישות פורטל" — the customers tab,
  // their own customers (the server scopes GET /portal-access the same way).
  if (key === PORTAL_ACCESS_SCREEN && access.capabilities.isAccountManager) return true;
  if (ADMIN_ONLY_SCREENS.has(key)) return false;
  // D-335: a screen the server keeps for the OTE manager opens only by the
  // employee's own "view" / "edit" override (its API honours it).
  if (SERVER_ADMIN_ONLY_SCREENS.has(key)) return openedByOverride(access, key) && access.screens.has(key);
  return access.screens.has(key);
}
const PORTAL_ACCESS_SCREEN = 'screen:portalAccess';

export function hasScope(access: ScreenAccess, key: string): boolean {
  return access.isAdmin || access.scopes.has(key);
}

// ─── Paths ───────────────────────────────────────────────────────────────────

type LegacyRule = (role: NavRole) => boolean;
const staffOnly: LegacyRule = (r) => r === 'admin' || r === 'agent';
const internalOnly: LegacyRule = (r) => !!r && r !== 'customer_viewer';

/**
 * Detail pages that had their own role guard in App.tsx before screen
 * permissions (AgentRoute / InternalRoute). Every other detail page of a nav
 * screen was open to every signed-in role.
 */
const DETAIL_LEGACY: Record<string, LegacyRule> = {
  leads: staffOnly,
  quotes: staffOnly,
  integrations: staffOnly,
  engagements: internalOnly,
};

function pathnameOf(path: string): string {
  const cut = path.search(/[?#]/);
  const p = cut >= 0 ? path.slice(0, cut) : path;
  return p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;
}

/**
 * The screen an in-app path belongs to: the nav item with the longest
 * matching path. `exact` is the screen's own page; otherwise it is a page
 * under it (`/projects/<id>`). `null` for paths outside the nav (home,
 * profile, the calendar, the portal).
 */
export function screenForPath(path: string): { def: ScreenDef; exact: boolean } | null {
  if (!path || !path.startsWith('/')) return null;
  const pathname = pathnameOf(path);
  let best: ScreenDef | null = null;
  for (const s of screenCatalog()) {
    if (s.to === '/') continue;
    if ((pathname === s.to || pathname.startsWith(`${s.to}/`)) && (!best || s.to.length > best.to.length)) best = s;
  }
  return best ? { def: best, exact: pathname === best.to } : null;
}

/**
 * May this person open this in-app path? A screen's own page needs the
 * screen. A page under it (a project, a quote) is open with the screen; when
 * the role never had the screen by default (a customer manager on a project
 * page), the page keeps the rule it had before screen permissions; when the
 * screen was taken away from the role (a group or personal "חסום"), its pages
 * close too.
 */
export function canOpenPath(access: ScreenAccess, path: string): boolean {
  if (access.internalOnly && internalOnlyOffNavClosed(path)) return false;
  const hit = screenForPath(path);
  if (!hit) return true;
  if (access.portalOnly) return false;
  const { def, exact } = hit;
  // P-33c (D-185): no page of any screen outside her five, detail pages included.
  if (access.internalOnly) return canScreen(access, def.key);
  if (canScreen(access, def.key)) return true;
  if (exact) return false;
  if (access.roleDefaults.has(def.key)) return false;
  const legacy = DETAIL_LEGACY[def.navId];
  // The portal identity has no internal page at all: every internal route is
  // `requirePortalOnly` on the server (its data is `/api/portal/*`).
  return legacy ? legacy(access.role) : internalOnly(access.role);
}

/**
 * FIX-B15 (QA-P33bc) — pages outside the nav that an internal-only employee
 * never opens (no matching screen, so the screen rule alone does not close
 * them): the monthly service log and search. They go home, no 403, no empty page.
 */
const INTERNAL_ONLY_OFFNAV_CLOSED = ['/service-log', '/search'];
export function internalOnlyOffNavClosed(path: string): boolean {
  if (!path || !path.startsWith('/')) return false;
  const p = pathnameOf(path);
  return INTERNAL_ONLY_OFFNAV_CLOSED.some((x) => p === x || p.startsWith(`${x}/`));
}

/**
 * DQ-018 — a customer identity has no internal screen (SEC-2a, D-91 ב5), and that
 * includes the internal pages outside the nav (the calendar, the monthly service
 * log, search): they used to open inside the internal shell, every request 403.
 * Only the profile stays (the portal's bell links to /profile#notifications).
 */
const PORTAL_OFFNAV_OPEN = ['/', '/profile'];
export function portalOffNavClosed(path: string): boolean {
  if (!path || !path.startsWith('/')) return false;
  const p = pathnameOf(path);
  return !PORTAL_OFFNAV_OPEN.includes(p) && !screenForPath(p);
}

/** The portal identity's home: a closed address lands in the customer portal. */
export const PORTAL_HOME = '/portal';
/** The portal tab that holds the customer's requests ("פניות"). */
export const PORTAL_REQUESTS = '/portal?tab=requests';

/**
 * Where a closed address goes, silently (`ScreenGate`). Staff and the
 * customer manager: home (`/`). The customer_viewer: the portal, and a
 * ticket or conversation address (theirs is a request in the portal) opens
 * the portal's requests tab.
 */
export function closedPathTarget(role: NavRole, path: string, portalOnly = false, internalOnly = false): string {
  // FIX-B15 (QA-P33bc): an internal-only employee's ticket address (a link in
  // "היום שלי", a chat or a notification) is her task: /tickets/:id -> /tasks/:id.
  if (internalOnly && !portalOnly) {
    const m = /^\/tickets\/([^/?#]+)\/?$/.exec(pathnameOf(path));
    return m ? `/tasks/${m[1]}` : '/';
  }
  if (role !== 'customer_viewer' && !portalOnly) return '/';
  // DQ-083: a customer's link to ONE request (the e-mail "open the request" button
  // is /tickets/:id, a shared /discussions?ticket=:id) opens that request in the
  // portal, not just the list. The portal sends a conversation's id on to its tab.
  const one = /^\/tickets\/([^/?#]+)$/.exec(pathnameOf(path))?.[1]
    ?? (pathnameOf(path) === '/discussions' ? new URLSearchParams(path.split('?')[1]?.split('#')[0] || '').get('ticket') : null);
  if (one) return `${PORTAL_REQUESTS}&request=${encodeURIComponent(one)}`;
  const hit = screenForPath(path);
  const id = hit?.def.navId;
  return id === 'tickets' || id === 'conversations' ? PORTAL_REQUESTS : PORTAL_HOME;
}

/** D-335 — `screen_access` of `/me`: only `screen:` keys with a known level. */
function parseScreenAccess(raw: unknown): Record<string, ScreenAccessLevel> {
  const out: Record<string, ScreenAccessLevel> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (k.startsWith(SCREEN_PREFIX) && (v === 'hidden' || v === 'view' || v === 'edit')) out[k] = v;
  }
  return out;
}

/** Normalise the `/me` body (wrapped in `{ success, data }` or bare). */
export function parseMyPermissions(body: unknown): MyPermissions | null {
  const raw = body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)
    ? (body as { data: unknown }).data
    : body;
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (!Array.isArray(r.screens)) return null;
  const caps = r.capabilities && typeof r.capabilities === 'object' ? (r.capabilities as Record<string, unknown>) : null;
  const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
  return {
    isAdmin: r.isAdmin === true,
    screens: (r.screens as unknown[]).filter((x): x is string => typeof x === 'string'),
    scopes: Array.isArray(r.scopes) ? (r.scopes as unknown[]).filter((x): x is string => typeof x === 'string') : [],
    ...(r.portalOnly === true || r.isOteEmployee === false ? { portalOnly: true } : {}),
    ...(typeof r.isOteEmployee === 'boolean' ? { isOteEmployee: r.isOteEmployee } : {}),
    ...(r.internal_only === true ? { internalOnly: true } : {}),
    ...(typeof r.internal_work_engagement_id === 'string' ? { internalWorkEngagementId: r.internal_work_engagement_id } : {}),
    ...(typeof r.internal_work_organization_id === 'string' ? { internalWorkOrganizationId: r.internal_work_organization_id } : {}),
    // `screen_access` from the server; `screenAccess` from this device's cached answer (usePermissions) - a reload must not lose it.
    ...((r.screen_access ?? r.screenAccess) && typeof (r.screen_access ?? r.screenAccess) === 'object'
      ? { screenAccess: parseScreenAccess(r.screen_access ?? r.screenAccess) } : {}),
    // FIX-PERMS-SCOPE: `data_scopes` from the server; `dataScopes` from this device's cached answer.
    ...(Array.isArray(r.data_scopes ?? r.dataScopes) ? { dataScopes: ids(r.data_scopes ?? r.dataScopes) } : {}),
    ...(caps
      ? {
          capabilities: {
            isGeneralManager: caps.isGeneralManager === true,
            isAccountManager: caps.isAccountManager === true,
            managedOrgIds: ids(caps.managedOrgIds),
            managedProjectIds: ids(caps.managedProjectIds),
            teamLeadProjectIds: ids(caps.teamLeadProjectIds),
            canManageAllLeads: caps.canManageAllLeads === true,
            canViewOthersAttendance: caps.canViewOthersAttendance === true,
            ...(typeof caps.canEditOthersAttendance === 'boolean' ? { canEditOthersAttendance: caps.canEditOthersAttendance } : {}),
            canCreateEmployeeLogin: caps.canCreateEmployeeLogin === true,
            canUseObligations: caps.canUseObligations === true,
            canViewDebtAging: caps.canViewDebtAging === true,
          },
        }
      : {}),
  };
}
